import { OutputInfo } from './../models/output-info.model';
import { OperationPoint } from './../models/operation-point.model';
import { FrequencyControl } from './../models/frequency-control.model';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SelectedPump, SelectedUnits } from '../models';
import { ViscosityControl } from '../models/viscosity-control.model';

@Injectable({
  providedIn: 'root',
})
export class CalculationSelectionService {
  private defaultSelectedPdfProperties = [
    'capacity',
    'differentialPressure',
    'pumpSpeed',
    'allowedSpeedMin',
    'allowedSpeedMax',
    'operatingPower',
    'operatingTorque',
    'npshr',
    'maxStartingTorque',
    'maxAxialLoad',
    'maxOperatingPower',
    'motorSpeed',
    'frequency',
    'requiredMotorPowerSelfVentilated',
    'requiredMotorPowerForcedVentilated',
    'startingTorqueRatedTorque',
    'requiredRatedMotorPower',
  ];

  private selectedPump = new SelectedPump();
  private pumpSelectionSubject = new Subject<SelectedPump>();
  private selectedUnits = new SelectedUnits();
  private unitSelectionSubject = new Subject<SelectedUnits>();
  private selectedOutputInfo = new OutputInfo();
  private outputInfoSubject = new Subject<OutputInfo>();
  private frequencyControl = new FrequencyControl();
  private frequencyControlSubject = new Subject<FrequencyControl>();
  private viscosityControl = new ViscosityControl();
  private viscosityControlSubject = new Subject<ViscosityControl>();
  private startingTorqueRatedTorque: number = 0;
  private startingTorqueRatedTorqueSubject = new Subject<number>();
  private operationPoints: OperationPoint[] = [];
  private operationPointsSubject = new Subject<OperationPoint[]>();
  private outputInfo = new OutputInfo();
  private selectedPdfProperties: string[] = [];
  private selectedPdfPropertiesSubject = new Subject<string[]>();

  public unitSelectionChanged = this.unitSelectionSubject.asObservable();
  public pumpSelectionChanged = this.pumpSelectionSubject.asObservable();
  public ouputInfoChanged = this.outputInfoSubject.asObservable();
  public frequencyControlChanged = this.frequencyControlSubject.asObservable();
  public viscosityControlChanged = this.viscosityControlSubject.asObservable();
  public startingTorqueRatedTorqueChanged = this.startingTorqueRatedTorqueSubject.asObservable();
  public operationPointsChanged = this.operationPointsSubject.asObservable();
  public outputInfoChanged = this.outputInfoSubject.asObservable();
  public selectedPdfPropertiesChanged = this.selectedPdfPropertiesSubject.asObservable();

  constructor() {
    this.setSelectedPdfProperties(this.defaultSelectedPdfProperties);
  }

  selectPump(pump: SelectedPump) {
    this.selectedPump = pump;

    this.pumpSelectionSubject.next(this.selectedPump);
  }

  selectUnits(units: SelectedUnits) {
    this.selectedUnits = units;

    this.unitSelectionSubject.next(this.createUnitSelectionCopy());
  }

  selectOutputInfo(outputInfo: OutputInfo) {
    this.selectedOutputInfo = outputInfo;

    this.outputInfoSubject.next(this.selectedOutputInfo);
  }

  selectStartingTorqueRatedTorque(startingTorqueRatedTorque: number) {
    this.startingTorqueRatedTorque = startingTorqueRatedTorque;

    this.startingTorqueRatedTorqueSubject.next(this.startingTorqueRatedTorque);
  }

  setFrequencyControl(frequencyControl: FrequencyControl) {
    this.frequencyControl = frequencyControl;

    this.frequencyControlSubject.next(this.frequencyControl);
  }

  setViscosityControl(viscosityControl: ViscosityControl) {
    this.viscosityControl = viscosityControl;

    this.viscosityControlSubject.next(this.viscosityControl);
  }

  setOperationPoints(operationPoints: OperationPoint[]) {
    this.operationPoints = operationPoints;

    this.operationPointsSubject.next(this.operationPoints);
  }

  setOutputInfo(outputInfo: OutputInfo) {
    this.outputInfo = outputInfo;

    this.outputInfoSubject.next(this.createOutputInfoCopy());
  }

  setSelectedPdfProperties(properties: string[]) {
    this.selectedPdfProperties = properties;

    this.selectedPdfPropertiesSubject.next(this.selectedPdfProperties);
  }

  getSelectedPump() {
    return this.selectedPump;
  }

  getSelectedUnits() {
    return this.createUnitSelectionCopy();
  }

  getSelectedOutputInfo(): OutputInfo {
    return this.selectedOutputInfo;
  }

  getFrequencyControl() {
    return this.frequencyControl;
  }

  getViscosityControl() {
    return this.viscosityControl;
  }

  getStartingTorqueRatedTorque() {
    return this.startingTorqueRatedTorque;
  }

  getOperationPoints() {
    return this.operationPoints;
  }

  getOutputInfo() {
    return this.createOutputInfoCopy();
  }

  getSelectedPdfProperties() {
    return this.selectedPdfProperties;
  }

  private createOutputInfoCopy() {
    const newOutputInfo = new OutputInfo();
    newOutputInfo.customer = this.outputInfo.customer;
    newOutputInfo.name = this.outputInfo.name;
    newOutputInfo.notes = this.outputInfo.notes;
    newOutputInfo.position = this.outputInfo.position;
    newOutputInfo.reference = this.outputInfo.reference;
    newOutputInfo.typeCode = this.outputInfo.typeCode;

    return newOutputInfo;
  }

  private createUnitSelectionCopy() {
    const newUnitSelection = new SelectedUnits();
    newUnitSelection.capacity = this.selectedUnits.capacity;
    newUnitSelection.pressure = this.selectedUnits.pressure;
    newUnitSelection.unitSystem = this.selectedUnits.unitSystem;

    return newUnitSelection;
  }
}
